import Axios, { type AxiosError, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { RefreshQueue } from './refresh-queue'
import type { AuthConfig } from './types'

function headerValue(response: AxiosResponse, name: string): string | null {
	const value = response.headers?.[name]
	return typeof value === 'string' && value !== '' ? value : null
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
	_retry?: boolean
}

interface RefreshFlow<T> {
	refresh(): Promise<T>
	applyRefreshResult?(request: RetryableRequestConfig, value: T): void
	cleanupAfterFailure?(): Promise<void>
	rejectionError(originalError: AxiosError, refreshError: unknown): unknown
}

function createRefreshOn401Handler<T>(
	instance: AxiosInstance,
	shouldSkipAuth: (url: string | undefined) => boolean,
	onAuthFailure: (error: unknown) => void,
	flow: RefreshFlow<T>,
) {
	const queue = new RefreshQueue<T>()

	return async (error: AxiosError) => {
		const originalRequest = error.config as RetryableRequestConfig | undefined

		if (
			!originalRequest ||
			error.response?.status !== 401 ||
			originalRequest._retry ||
			shouldSkipAuth(originalRequest.url)
		) {
			return Promise.reject(error)
		}

		originalRequest._retry = true

		if (queue.isRefreshing) {
			let value: T
			try {
				value = await queue.enqueue()
			} catch (refreshError) {
				return Promise.reject(flow.rejectionError(error, refreshError))
			}
			flow.applyRefreshResult?.(originalRequest, value)
			return instance(originalRequest)
		}

		queue.begin()

		try {
			const value = await flow.refresh()
			queue.resolveAll(value)
			flow.applyRefreshResult?.(originalRequest, value)
			return instance(originalRequest)
		} catch (refreshError) {
			queue.rejectAll(refreshError)
			await flow.cleanupAfterFailure?.()
			const rejection = flow.rejectionError(error, refreshError)
			onAuthFailure(rejection)
			return Promise.reject(rejection)
		} finally {
			queue.end()
		}
	}
}

export function setupAuthInterceptors(
	instance: AxiosInstance,
	config: AuthConfig,
): { requestId: number; responseId: number } {
	const { skipAuthPaths, onAuthFailure, tokenHandling, cookieRefresh } = config

	function shouldSkipAuth(url: string | undefined): boolean {
		if (!url) return false
		return skipAuthPaths.some((path) => {
			const index = url.indexOf(path)
			if (index === -1) return false
			const nextChar = url[index + path.length]
			return nextChar === undefined || nextChar === '/' || nextChar === '?'
		})
	}

	if (tokenHandling) {
		const { refreshUrl, tokenStorage } = tokenHandling

		const refreshInstance = Axios.create()

		const requestId = instance.interceptors.request.use(async (requestConfig) => {
			if (shouldSkipAuth(requestConfig.url)) {
				return requestConfig
			}

			const accessToken = await tokenStorage.getAccessToken()
			if (accessToken) {
				requestConfig.headers.Authorization = `Bearer ${accessToken}`
			}

			return requestConfig
		})

		const responseId = instance.interceptors.response.use(
			// The backend delivers session tokens in response headers (header mode, `Prefer: no-cookies`);
			// only POST /sessions sets both, so their presence marks a fresh session to persist.
			async (response) => {
				const accessToken = headerValue(response, 'authorization')
				const refreshToken = headerValue(response, 'refresh-token')
				if (accessToken && refreshToken) {
					await tokenStorage.setTokens(accessToken, refreshToken)
				}
				return response
			},
			createRefreshOn401Handler<string>(instance, shouldSkipAuth, onAuthFailure, {
				refresh: async () => {
					const refreshToken = await tokenStorage.getRefreshToken()
					if (!refreshToken) {
						throw new Error('No refresh token available')
					}

					const response = await refreshInstance.patch(`${instance.defaults.baseURL}${refreshUrl}`, undefined, {
						headers: { 'Refresh-Token': refreshToken, Prefer: 'no-cookies' },
					})

					const newAccessToken = headerValue(response, 'authorization')
					if (!newAccessToken) {
						throw new Error('No access token in refresh response')
					}

					// No refresh-token rotation in v1 (ADR 0015) — the existing token stays valid.
					await tokenStorage.setTokens(newAccessToken, refreshToken)

					return newAccessToken
				},
				applyRefreshResult: (request, accessToken) => {
					request.headers.Authorization = `Bearer ${accessToken}`
				},
				cleanupAfterFailure: () => tokenStorage.clearTokens(),
				rejectionError: (_originalError, refreshError) => refreshError,
			}),
		)

		return { requestId, responseId }
	}

	const requestId = instance.interceptors.request.use((requestConfig) => requestConfig)

	if (cookieRefresh) {
		const { refreshUrl } = cookieRefresh

		const refreshInstance = Axios.create()

		const responseId = instance.interceptors.response.use(
			(response) => response,
			createRefreshOn401Handler<void>(instance, shouldSkipAuth, onAuthFailure, {
				refresh: async () => {
					await refreshInstance.patch(`${instance.defaults.baseURL}${refreshUrl}`, undefined, {
						withCredentials: true,
					})
				},
				rejectionError: (originalError) => originalError,
			}),
		)

		return { requestId, responseId }
	}

	const responseId = instance.interceptors.response.use(
		(response) => response,
		(error) => {
			const originalRequest = error.config as RetryableRequestConfig | undefined

			if (!originalRequest || error.response?.status !== 401 || shouldSkipAuth(originalRequest.url)) {
				return Promise.reject(error)
			}

			onAuthFailure(error)
			return Promise.reject(error)
		},
	)

	return { requestId, responseId }
}
